import Phaser from "phaser";
import { GAME_MODES, SOUND_KEYS, TIMING } from "../../constants/game";
import { Ghost, NumberPill } from "../../types/game";
import { SoundManager } from "../SoundManager";

/**
 * CollisionManager
 * Handles all collision detection and callbacks
 */
export class CollisionManager {
  private scene: Phaser.Scene;
  private increaseSmallDotsCount?: (count: number) => void;
  private increasePowerPillsCount?: (count: number) => void;
  private increaseCorrectAnswersCount?: (count: number) => void;
  private onPillEaten?: (answer: number, correctAnswer: number) => void;
  private onWrongAnswer?: () => void;
  private pacmanDiesCallback?: () => void;
  private getCorrectAnswer?: () => number | undefined;
  private getCurrentMode?: () => string;
  private respawnGhostCallback?: (ghost: Ghost) => void;
  private soundManager?: SoundManager;
  private deathInProgress: boolean = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Reset the collision manager state
   */
  reset(): void {
    this.deathInProgress = false;
  }

  /**
   * Set callbacks for score and game events
   */
  setCallbacks(callbacks: {
    increaseSmallDotsCount?: (count: number) => void;
    increasePowerPillsCount?: (count: number) => void;
    increaseCorrectAnswersCount?: (count: number) => void;
    onPillEaten?: (answer: number, correctAnswer: number) => void;
    onWrongAnswer?: () => void;
    pacmanDiesCallback?: () => void;
    getCorrectAnswer?: () => number | undefined;
    getCurrentMode?: () => string;
    respawnGhostCallback?: (ghost: Ghost) => void;
    soundManager?: SoundManager;
  }): void {
    this.increaseSmallDotsCount = callbacks.increaseSmallDotsCount;
    this.increasePowerPillsCount = callbacks.increasePowerPillsCount;
    this.increaseCorrectAnswersCount = callbacks.increaseCorrectAnswersCount;
    this.onPillEaten = callbacks.onPillEaten;
    this.onWrongAnswer = callbacks.onWrongAnswer;
    this.pacmanDiesCallback = callbacks.pacmanDiesCallback;
    this.getCorrectAnswer = callbacks.getCorrectAnswer;
    this.getCurrentMode = callbacks.getCurrentMode;
    this.respawnGhostCallback = callbacks.respawnGhostCallback;
    this.soundManager = callbacks.soundManager;
  }

  /**
   * Handle Pacman eating a dot
   */
  eatDot(
    _pacman: Phaser.Physics.Arcade.Sprite,
    dot: Phaser.Physics.Arcade.Sprite
  ): void {
    dot.disableBody(true, true);

    // Increase score when eating a dot
    if (this.increaseSmallDotsCount) {
      this.increaseSmallDotsCount(1); // Award 1 point for eating a dot
    }
  }

  /**
   * Handle Pacman eating a power pill
   */
  eatPowerPill(
    _pacman: Phaser.Physics.Arcade.Sprite,
    powerPill: Phaser.Physics.Arcade.Sprite,
    setGhostsScaredCallback: () => void
  ): void {
    powerPill.disableBody(true, true);

    // Increase score when eating a power pill
    if (this.increasePowerPillsCount) {
      this.increasePowerPillsCount(1); // Award 1 point for eating a power pill
    }

    // Play power pill sound
    if (this.soundManager) {
      this.soundManager.playSound(SOUND_KEYS.CREDIT_SOUND);
    }

    // Trigger scared mode
    setGhostsScaredCallback();
  }

  /**
   * Handle Pacman eating a number pill
   */
  eatPill(_pacman: Phaser.Physics.Arcade.Sprite, pill: NumberPill): void {
    if (!pill || pill.number == null || this.deathInProgress) return;

    const answer = pill.number;
    const correctAnswer = this.getCorrectAnswer
      ? this.getCorrectAnswer()
      : undefined;

    // Remove the pill from physics group and destroy it
    if (pill.body) {
      const body = pill.body as
        | Phaser.Physics.Arcade.Body
        | Phaser.Physics.Arcade.StaticBody;
      this.scene.physics.world.disableBody(body);
    }
    // Destroy pill (group cleanup handled by PillManager)
    pill.destroy();

    // Check if answer is correct BEFORE calling callbacks
    const isCorrect = correctAnswer !== undefined && answer === correctAnswer;

    // Call onPillEaten callback - this will regenerate pills for both correct and wrong answers
    if (this.onPillEaten) {
      this.onPillEaten(answer, correctAnswer || 0);
    }

    // Check if answer is correct
    if (isCorrect) {
      // Correct answer - award points
      if (this.increaseCorrectAnswersCount) {
        this.increaseCorrectAnswersCount(1);
      }
      if (this.soundManager) {
        this.soundManager.playSound(SOUND_KEYS.CORRECT_ANSWER);
      }
    } else {
      // Wrong answer - kill pacman
      this.deathInProgress = true;
      if (this.onWrongAnswer) {
        this.onWrongAnswer();
      }
      if (this.pacmanDiesCallback) {
        this.pacmanDiesCallback();
      }
    }
  }

  /**
   * Handle collision between Pacman and a ghost
   */
  handlePacmanGhostCollision(
    _pacman: Phaser.Physics.Arcade.Sprite,
    ghost: Ghost
  ): void {
    // Prevent multiple collision processing during death
    if (this.deathInProgress) return;

    const currentMode = this.getCurrentMode
      ? this.getCurrentMode()
      : GAME_MODES.SCATTER;

    if (currentMode === GAME_MODES.SCARED && !ghost.hasBeenEaten) {
      // Pacman eats the ghost in scared mode (hasBeenEaten = false means vulnerable)
      // Play ghost eating sound
      if (this.soundManager) {
        this.soundManager.playSound(SOUND_KEYS.EATING_GHOST);
      }

      ghost.setActive(false);
      ghost.setVisible(false);

      // Respawn ghost after delay
      this.scene.time.delayedCall(TIMING.GHOST_RESPAWN_DELAY, () => {
        if (this.respawnGhostCallback) {
          this.respawnGhostCallback(ghost);
        }
      });
    } else if (ghost.hasBeenEaten) {
      // Ghost kills Pacman in normal mode (hasBeenEaten = true means dangerous)
      this.deathInProgress = true;
      if (this.pacmanDiesCallback) {
        this.pacmanDiesCallback();
      }
    }
  }
}
